/**
 * React Query configuration for optimal caching and performance
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query';

/**
 * Default options for React Query
 * Optimized for app store data characteristics
 */
const queryClientDefaultOptions: DefaultOptions = {
  queries: {
    // How long data stays fresh before refetch is needed
    // Increased from 5 to 10 minutes for better caching
    staleTime: 10 * 60 * 1000, // 10 minutes

    // How long inactive data stays in cache
    // Increased from 10 to 30 minutes to reduce refetches
    gcTime: 30 * 60 * 1000, // 30 minutes (previously cacheTime)
    
    // Retry configuration
    retry: (failureCount, error: any) => {
      // Don't retry on client errors (4xx)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry server errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Disable automatic refetching
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: false,
    
    // Network mode
    networkMode: 'online',
  },
  mutations: {
    // Retry mutations once on network errors
    retry: (failureCount, error: any) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
    networkMode: 'online',
  },
};

/**
 * Create optimized QueryClient instance
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: queryClientDefaultOptions,
  });
}

/**
 * Query key factory for consistent cache keys
 * Prevents typos and provides type safety
 */
export const queryKeys = {
  // Search
  search: (query: string, country?: string) => ['search', query, country] as const,
  searchResult: (id: string) => ['search-result', id] as const,
  
  // Apps
  app: (appId: number, country?: string) => ['app', appId, country] as const,
  appMetadata: (appId: number, country?: string) => ['app-metadata', appId, country] as const,
  appRankings: (appId: number, country?: string) => ['app-rankings', appId, country] as const,
  appRevenue: (appId: number) => ['app-revenue', appId] as const,
  appReviews: (appId: number, country?: string) => ['app-reviews', appId, country] as const,
  
  // New apps
  newApps: (country?: string) => ['new-apps', country] as const,
  
  // Similar
  similarApps: (appId: number, country?: string) => ['similar-apps', appId, country] as const,
  
  // Monitors
  monitors: () => ['monitors'] as const,
  monitorSummary: () => ['monitor-summary'] as const,
  
  // Screenshots
  screenshots: (query: string) => ['screenshots', query] as const,
  screenshotsSimilar: (id: string) => ['screenshots-similar', id] as const,
  screenshotsFacets: () => ['screenshots-facets'] as const,
  
  // Rankings
  rankings: (country: string, category?: string) => ['rankings', country, category] as const,
  
  // Developer
  developer: (developerId: string) => ['developer', developerId] as const,
  developerApps: (developerId: string) => ['developer-apps', developerId] as const,
  
  // Chat
  chatMessages: (threadId: string) => ['chat-messages', threadId] as const,
} as const;

